/* eslint-disable @typescript-eslint/no-shadow */
import { createStyles, Group } from "@mantine/core";

import { Button, ButtonLink } from "~/domains/design-system/Button";
import Dialog from "~/domains/design-system/components/Dialog/Dialog";
import { Text } from "~/domains/design-system/Text";
import useOpenIntercom from "~/components/Intercom/useOpenItercom";
import { mailToSupport } from "~/components/utils/mailToSupport";
import { toSafeMarkup } from "~/domains/common/utils/toSafeMarkup";

import { ContestManageModalBody, ContestManageModalTitle } from '../commonStyles';

type ModalActionOpenChatSupport = {
  action: 'openChatSupport';
  message: {
    subject: string;
    body: string;
  };
};

type ModalActionClose = {
  action: 'closeModal';
};

export type ModalActionType = {
  label: string;
} & (
  | {
      href: string;
    }
  | ModalActionOpenChatSupport
  | ModalActionClose
);

const useStyles = createStyles((theme) => ({
  description: {
    paddingTop: theme.other.spacing._8,
    paddingBottom: theme.other.spacing._16,
    color: theme.colors.gray[7],

    '& a': {
      color: theme.colorScheme === 'dark' ? theme.colors.splash[5] : theme.colors.splash[7],
      textDecoration: 'none',

      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
}));

function ModalAction({ action, onClose }: { action: ModalActionType; onClose: () => void }) {
  const { openIntercom, isIntercomAvailable } = useOpenIntercom();

  const ActionHandlers: {
    [K in 'openChatSupport' | 'closeModal']: (
      action: Extract<ModalActionType, { action: K }>,
      onClose: () => void
    ) => void;
  } = {
    openChatSupport: (action: ModalActionOpenChatSupport) => {
      const { body, subject } = action.message;

      if (isIntercomAvailable) {
        openIntercom(body);
      } else {
        mailToSupport({
          subject,
          body,
        });
      }
    },
    closeModal: (action: ModalActionClose, onClose: () => void) => {
      onClose();
    },
  };

  if ('href' in action) {
    return <ButtonLink href={action.href}>{action.label}</ButtonLink>;
  }

  const actionHandler = ActionHandlers[action.action];
  if (!actionHandler) {
    return null;
  }

  return (
    <Button
      variant="primary-fill"
      onClick={() => {
        // @ts-expect-error: not smart enought to fix this
        actionHandler(action, onClose);
      }}
    >
      {action.label}
    </Button>
  );
}

function ModalActions({ actions, onClose }: { actions: ModalActionType[]; onClose: () => void }) {
  const { theme } = useStyles();

  return (
    <Group mt={theme.other.spacing._16} spacing={theme.other.spacing._16} grow>
      {actions.map((action) => (
        <ModalAction key={action.label} action={action} onClose={onClose} />
      ))}
    </Group>
  );
}

function GenericModal({
  isOpen,
  onClose,
  title,
  description,
  actions,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  actions: ModalActionType[];
}) {
  const { classes } = useStyles();

  return (
    <Dialog
      type={{
        desktop: 'modal',
        mobile: 'modal-full-screen',
      }}
      isOpen={isOpen}
      onClose={onClose}
    >
      <ContestManageModalBody>
        <ContestManageModalTitle>{title}</ContestManageModalTitle>
        {description && (
          <Text
            tag="div"
            className={classes.description}
            dangerouslySetInnerHTML={{ __html: toSafeMarkup(description) }}
            variant="body-large"
          />
        )}
        {actions && <ModalActions actions={actions} onClose={onClose} />}
      </ContestManageModalBody>
    </Dialog>
  );
}

export default GenericModal;
